const en = {
  common: {
    login: 'Log in',
    register: 'Sign up free',
    backHome: 'Back to home',
    user: 'User',
    admin: 'Admin',
    userMenu: 'User menu',
    logout: 'Log out',
    cancel: 'Cancel',
    delete: 'Delete',
    loading: 'Loading…',
    unknownError: 'Unknown error',
    emptyValue: '—',
    duration: {
      seconds: '{{value}}s',
      minutes: '{{value}} min',
      hours: '{{value}} h',
      hoursMinutes: '{{hours}} h {{minutes}} min',
    },
    footerCta: 'This project is open source and contributions are welcome',
    repoLink: 'Project repository',
    author: 'Maintainer',
    reload: 'Reload page',
    loadFailedTitle: 'Something went wrong',
    loadFailedBody: 'This page could not be rendered. Reloading usually fixes it.',
    openDrawer: 'Open navigation',
    togglePasswordVisibility: 'Toggle password visibility',
  },
  language: {
    label: 'Language',
    en: 'EN',
    zhCN: '中文',
  },
  navigation: {
    groups: {
      monitor: 'Monitoring',
      settings: 'Settings',
      admin: 'Administration',
    },
    dashboard: 'Dashboard',
    tasks: 'Monitor tasks',
    publicTasks: 'Public tasks',
    mySubscriptions: 'Subscriptions',
    logs: 'Monitor logs',
    emailConfig: 'Email notifications',
    blacklist: 'Blocked domains',
    users: 'Users',
  },
  shell: {
    lastCheck: 'Last check {{time}}',
    noChecks: 'No checks recorded',
  },
  home: {
    navDocs: 'Documentation',
    navLogin: 'Log in',
    navRegister: 'Sign up free',
    heroTitleLine1: 'Know when the page',
    heroTitleLine2: 'actually changes',
    heroSubtitle:
      'Point an XPath at one region, set a check interval, and get an email when its text changes.',
    heroPrimary: 'Start monitoring',
    heroSecondary: 'Log in',
    heroFacts: 'XPath selection · Chromium rendering · SMTP delivery',
    preview: {
      taskName: 'Graduate school notices',
      url: 'gs.example.edu/notices',
      interval: 'every 5 min',
      checkedLabel: 'Last check',
      checkedValue: '3 min ago',
      recipientLabel: 'Notifies',
      recipientValue: 'admin@example.edu',
      changeLabel: 'Change detected',
      changeTime: '09:41',
      lineOld: 'Spring 2027 admissions notice published',
      lineNew: 'Application window opens 12 January',
      emailLabel: 'Notification',
      emailValue: 'sent to admin@example.edu',
    },
    license: 'CC BY-NC 4.0',
    featuresTitle: 'Configure checks and review changes',
    featuresSubtitle:
      'Choose the page region and interval, then review collection logs and email notifications.',
    features: [
      {
        key: 'realtime',
        title: 'JavaScript-rendered pages',
        description: 'Chromium loads the page before reading the selected text, including content rendered by JavaScript.',
      },
      {
        key: 'notifications',
        title: 'Email on change',
        description: 'Configure your email account to receive a notification when a check detects changed text.',
      },
      {
        key: 'secure',
        title: 'Account access',
        description: 'Sign in to manage your own tasks and email settings.',
      },
      {
        key: 'performance',
        title: 'Collection logs',
        description: 'Review check times, extracted text and collection errors.',
      },
      {
        key: 'analysis',
        title: 'XPath selection',
        description: 'Select the part of the page to compare with an XPath expression.',
      },
      {
        key: 'schedule',
        title: 'Per-task intervals',
        description: 'Choose how often each task checks its target page.',
      },
    ],
    advantagesTitle: 'Manage tasks and subscriptions',
    advantages: [
      {
        key: 'setup',
        title: 'Browser-based configuration',
        description: 'Enter the page URL, XPath and check interval in the application.',
      },
      {
        key: 'scale',
        title: 'Public task subscriptions',
        description: 'Subscribe to public tasks in this instance and manage your subscriptions.',
      },
      {
        key: 'api',
        title: 'HTTP API',
        description: 'Use the authenticated API to manage tasks and read collection logs.',
      },
    ],
    ctaTitle: 'Start monitoring the pages you care about',
    ctaSubtitle: 'Create a task, choose a check interval and configure email to receive change notifications.',
    footerTagline: 'Scheduled web checks with XPath selection and email alerts',
  },
  login: {
    heroTitle: 'Checks run on schedule',
    heroSubtitle: 'Sign in to manage monitor tasks, read collection logs and set up email delivery.',
    facts: 'Scheduled checks · Collected text · Email on change',
    formTitle: 'Log in',
    formSubtitle: 'Enter your account details',
    username: 'Username',
    password: 'Password',
    submit: 'Log in',
    submitting: 'Signing in…',
    noAccount: 'No account yet?',
    registerNow: 'Create one',
    defaultError: 'Login failed. Check the username and password.',
  },
  register: {
    heroTitleStart: 'Create your',
    heroTitleAccent: 'monitoring account',
    heroSubtitle: 'An account on this instance holds your monitor tasks, subscriptions and email settings.',
    benefitsTitle: 'What an account holds',
    benefits: [
      'Scheduled page checks',
      'Email change notifications',
      'Per-task check intervals',
      'XPath text selection',
    ],
    formTitle: 'Create account',
    formSubtitle: 'Choose a username, email and password',
    username: 'Username',
    email: 'Email address',
    password: 'Password',
    confirmPassword: 'Confirm password',
    passwordStrength: 'Password strength',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    submitting: 'Creating account…',
    submit: 'Create account',
    existingAccount: 'Already have an account?',
    loginNow: 'Log in now',
    instanceNote: 'This account belongs to this WebMonitor instance.',
    validation: {
      required: 'Please fill in all required fields.',
      username: 'Username must be at least 3 characters long.',
      mismatch: 'The two passwords do not match.',
      password: 'Password must be at least 6 characters long.',
      email: 'Please enter a valid email address.',
      success: 'Account created. Redirecting to sign in.',
      failed: 'Registration failed. Please try again later.',
    },
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Monitor your web content changes in real time.',
    refresh: 'Refresh data',
    totalTasks: 'Total tasks',
    activeTasks: 'Active tasks',
    recentChanges: 'Recent changes',
    successRate: 'Success rate',
    allTasks: 'Tasks in your task list',
    runningTasks: 'Tasks currently enabled',
    detectedChanges: 'Content changes in the latest {{sample}} log entries',
    executionSuccessRate: 'Share of successful runs in the latest {{sample}} log entries',
    noSamples: 'No log entries yet',
    recentActivity: 'Recent monitoring activity',
    viewAll: 'View all',
    noActivity: 'No monitoring activity yet',
    noActivitySubtitle: 'Activity will appear here after you create monitor tasks.',
    taskLabel: 'Task',
    changed: 'Changed',
    unchanged: 'Unchanged',
    errorPrefix: 'Error',
    changeDetected: 'Content change detected',
    newContentPreview: 'New content preview',
    contentUnchanged: 'Content unchanged',
    quickActions: 'Quick actions',
    checksByTask: 'Checks by task',
    checkCount: 'Checks',
    changeCount: 'Changes',
    recentErrors: 'Recent errors',
    manageTasks: 'Manage monitor tasks',
    configureEmail: 'Configure email notifications',
    viewLogs: 'View detailed logs',
  },
  tasks: {
    title: 'Monitor tasks',
    subtitle: 'Manage and configure your web monitoring tasks.',
    createTask: 'Create task',
    blacklistTitle: 'Blacklist restrictions',
    blacklistItem1: 'Regular users cannot monitor domains blocked by the blacklist.',
    blacklistItem2: 'If you need to monitor a blocked site, ask an admin to remove the domain from the blacklist.',
    blacklistItem3: 'Admins can manage domains in the blacklist management page.',
    totalTasks: 'Total tasks',
    activeTasks: 'Active tasks',
    pausedTasks: 'Paused tasks',
    emailNotifications: 'Email notifications',
    taskList: 'Monitor task list',
    noTasks: 'No monitor tasks yet',
    noTasksSubtitle: 'Create your first task to start monitoring web content changes.',
    createFirstTask: 'Create first task',
    taskInfo: 'Task info',
    monitorConfig: 'Monitor config',
    interval: 'Interval',
    emailConfig: 'Email config',
    status: 'Status',
    actions: 'Actions',
    notSet: 'Not set',
    unknownConfig: 'Unknown config',
    running: 'Running',
    paused: 'Paused',
    testMonitor: 'Test monitor',
    editTask: 'Edit task',
    deleteTask: 'Delete task',
    editDialogTitle: 'Edit monitor task',
    createDialogTitle: 'Create new monitor task',
    taskName: 'Task name',
    monitorUrl: 'Monitor URL',
    xpathSelector: 'XPath selector',
    xpathHelper: 'XPath expression used to locate the monitored content',
    intervalSeconds: 'Check interval (seconds)',
    emailConfigNone: 'No notifications',
    emailConfigHelper: 'Leave as "No notifications" to collect and log changes without sending email.',
    addEmailConfigHint: 'No email config yet — the task will still collect and log, but cannot notify anyone.',
    taskDescription: 'Task description',
    taskDescriptionHelper: 'Optional description visible to other users when the task is public',
    publicTask: 'Public task',
    publicTaskHelper: 'Other users can subscribe to this task and receive change notifications',
    enableMonitoring: 'Enable monitoring',
    enableMonitoringHelper: 'Start monitoring this page immediately',
    updateTask: 'Update task',
    submitCreateTask: 'Create task',
    loadTasksError: 'Failed to load monitor tasks',
    deleteConfirmTitle: 'Delete monitor task',
    deleteConfirm: 'Are you sure you want to delete this monitor task?',
    testSuccess: 'Test succeeded!',
    contentLabel: 'Content',
    testFailed: 'Test failed',
    blacklistAlertTitle: 'Blacklist restriction',
    createFailed: 'Failed to create monitor task',
    updateFailed: 'Failed to update monitor task',
    deleteSuccess: 'Monitor task deleted',
    deleteFailed: 'Failed to delete monitor task',
    created: 'Monitor task created',
    updated: 'Monitor task updated',
  },
  logs: {
    title: 'Monitor logs',
    subtitle: 'View and analyze execution records for your monitoring tasks.',
    selectTask: 'Select monitor task',
    loadError: 'Failed to load monitor logs',
    totalLogs: 'Total logs',
    unchanged: 'Unchanged',
    changed: 'Changed',
    error: 'Error',
    tableTitle: 'Monitoring log records',
    checkTime: 'Checked at',
    status: 'Status',
    newContent: 'New content',
    errorInfo: 'Error message',
    noLogs: 'No monitor logs yet',
    noLogsSubtitle: 'This task has not run yet.',
    paginationSummary: 'Showing {{start}}–{{end}} of {{total}} records',
    emptySummary: '0–0 / {{total}}',
    rowsPerPage: 'Rows per page',
    selectTaskEmpty: 'Please select a monitor task',
    selectTaskSubtitle: 'Choose a task to view its execution logs.',
    noTasks: 'You have no monitor tasks yet. Create one to see its logs.',
  },
  emailConfig: {
    sslConfigs: 'SSL connections',
    receiverAddresses: 'Receiver addresses',
    title: 'Email configuration',
    subtitle: 'Configure the SMTP servers and recipients used for notifications.',
    addConfig: 'Add email config',
    emailConfigs: 'Email configs',
    sectionTitle: 'SMTP email configuration',
    sectionDescription:
      'Configure SMTP servers so the system can send notification emails when changes are detected. Multiple email configs are supported and can be selected when creating monitor tasks.',
    noConfigs: 'No email configurations yet',
    noConfigsSubtitle: 'Add your first email configuration to start using notifications.',
    createFirstConfig: 'Create first config',
    configDetails: 'Config details',
    senderEmail: 'Sender email',
    receiverEmail: 'Receiver email',
    actions: 'Actions',
    smtpSenderAccount: 'SMTP sender account',
    notificationReceiver: 'Notification receiver',
    testConnection: 'Test connection',
    editConfig: 'Edit config',
    deleteConfig: 'Delete config',
    operationFailed: 'Operation failed',
    editDialogTitle: 'Edit email configuration',
    createDialogTitle: 'Create email configuration',
    dialogSubtitle: 'Configure an SMTP server for notifications',
    configName: 'Config name',
    smtpServer: 'SMTP server',
    smtpPort: 'SMTP port',
    senderAddress: 'Sender email',
    smtpPassword: 'SMTP password',
    passwordHelper: 'Using an app-specific password is recommended for better security',
    passwordHelperEdit: 'Leave blank to keep the current password',
    receiverAddress: 'Receiver email',
    useSsl: 'Use SSL encryption',
    useSslHelper: 'Enable SSL/TLS to secure mail transmission',
    cancel: 'Cancel',
    updateConfig: 'Update config',
    createConfig: 'Create config',
    loadFailed: 'Failed to load email configurations',
    createSuccess: 'Email configuration created',
    updateSuccess: 'Email configuration updated',
    deleteSuccess: 'Email configuration deleted',
    deleteFailed: 'Failed to delete email configuration',
    deleteConfirmTitle: 'Delete email configuration',
    deleteConfirm: 'Are you sure you want to delete this email configuration?',
    testSuccess: 'Test email sent successfully',
    testFailed: 'Test email failed',
  },
  users: {
    disabledUsers: 'Disabled users',
    title: 'User management',
    subtitle: 'Manage user accounts and permissions for the system.',
    createUser: 'Create user',
    totalUsers: 'Total users',
    activeUsers: 'Active users',
    admins: 'Admins',
    listTitle: 'User list',
    guideText: 'Admins can create, edit, and delete user accounts. Note: you cannot delete your own account.',
    noUsers: 'No users yet',
    noUsersSubtitle: 'Create the first user to get started.',
    createFirstUser: 'Create first user',
    userInfo: 'User info',
    emailAddress: 'Email address',
    rolePermission: 'Role',
    subscriptionLimit: 'Subscription limit',
    accountStatus: 'Account status',
    actions: 'Actions',
    fullNameNotSet: 'Full name not set',
    admin: 'Admin',
    regularUser: 'Regular user',
    subscriptionUnit: '',
    enabled: 'Enabled',
    disabled: 'Disabled',
    editUser: 'Edit user',
    deleteUser: 'Delete user',
    deleteConfirmTitle: 'Delete user',
    deleteConfirm: 'Are you sure you want to delete this user?',
    loadFailed: 'Failed to load users',
    editDialogTitle: 'Edit user',
    createDialogTitle: 'Create user',
    editDialogSubtitle: 'Update user details and permissions',
    createDialogSubtitle: 'Add a new system user',
    username: 'Username',
    fullName: 'Full name',
    password: 'Password',
    passwordHelperEdit: 'Leave blank to keep the current password',
    passwordHelperCreate: 'Set a secure password',
    maxSubscriptions: 'Max subscriptions',
    maxSubscriptionsHelper: 'Maximum number of public tasks this user can subscribe to',
    adminPermission: 'Admin permission',
    adminPermissionHelper: 'Gives access to system management features',
    activeAccount: 'Active account',
    activeAccountHelper: 'Allows the user to sign in',
    updateUser: 'Update user',
    createUserAction: 'Create user',
    createSuccess: 'User created',
    updateSuccess: 'User updated',
    deleteSuccess: 'User deleted',
    cannotDeleteSelf: 'You cannot delete your own account',
  },
  blacklist: {
    exactDomains: 'Exact domains',
    partialDomains: 'Partial matches',
    title: 'Blacklist management',
    subtitle: 'Manage domains that regular users are not allowed to monitor.',
    addDomain: 'Add domain',
    totalDomains: 'Total domains',
    wildcardDomains: 'Wildcard domains',
    guideTitle: 'How it works',
    guideLine1: 'Regular users cannot monitor domains on the blacklist.',
    guideLine2: 'Admins can monitor any site, including blacklisted domains.',
    guideLine3: 'The blacklist supports wildcard matching, for example *.example.com blocks all subdomains.',
    guideLine4: 'Partial matching is supported, for example example blocks any domain containing example.',
    listTitle: 'Blacklisted domains',
    noDomains: 'No blacklisted domains yet',
    noDomainsSubtitle: 'Add domains that regular users should not be allowed to monitor.',
    addFirstDomain: 'Add first domain',
    domain: 'Domain',
    description: 'Description',
    status: 'Status',
    createdAt: 'Created at',
    actions: 'Actions',
    wildcard: 'Wildcard domain',
    enabled: 'Enabled',
    disabledLabel: 'Disabled',
    edit: 'Edit',
    delete: 'Delete',
    editDialogTitle: 'Edit blacklisted domain',
    createDialogTitle: 'Add blacklisted domain',
    domainPlaceholder: 'For example: example.com, *.example.com, example',
    descriptionPlaceholder: 'Optional description for this domain',
    enabledStatus: 'Enabled status',
    update: 'Update',
    create: 'Add',
    fetchFailed: 'Failed to fetch blacklist',
    updateSuccess: 'Blacklist updated successfully',
    createSuccess: 'Domain added to blacklist successfully',
    deleteConfirmTitle: 'Delete blacklisted domain',
    deleteConfirm: 'Are you sure you want to delete this blacklisted domain?',
    deleteSuccess: 'Blacklisted domain deleted successfully',
  },
  publicTasks: {
    title: 'Public task marketplace',
    subtitle: 'Discover and subscribe to monitoring tasks shared by other users.',
    publicTasks: 'Public tasks',
    mySubscriptions: 'My subscriptions',
    remainingSlots: 'Remaining slots',
    totalSubscriptions: 'Total subscriptions',
    usageLine3: 'You will receive email notifications when subscribed tasks detect content changes.',
    taskList: 'Public task list',
    noTasks: 'No public tasks yet',
    noTasksSubtitle: 'No users have shared public monitoring tasks yet.',
    taskInfo: 'Task info',
    creator: 'Creator',
    subscriptions: 'Subscriptions',
    interval: 'Interval',
    createdAt: 'Created at',
    actions: 'Actions',
    subscribe: 'Subscribe',
    unsubscribe: 'Unsubscribe',
    subscribeTask: 'Subscribe to task',
    unsubscribeTask: 'Unsubscribe',
    viewDetails: 'View details',
    taskDetails: 'Task details',
    noDescription: 'No description',
    monitorUrl: 'Monitor URL',
    subscriberCount: 'Subscribers',
    close: 'Close',
    chooseEmailConfig: 'Choose email configuration',
    chooseEmailConfigSubtitle:
      'Task subscriptions require email notifications. Choose the email configuration to use.',
    noEmailConfig:
      'You have not configured email notifications yet. Add an email configuration on the email configuration page before subscribing.',
    emailConfig: 'Email configuration',
    cancel: 'Cancel',
    confirmSubscribe: 'Confirm subscription',
    toggleFailed: 'Operation failed',
    subscribeWithEmailFailed: 'Failed to subscribe with this email configuration',
    subscribed: 'Subscribed successfully',
    unsubscribed: 'Subscription cancelled',
    createdByLabel: 'Created by',
    createdAtLabel: 'Created at',
    loadFailed: 'Failed to load the marketplace',
  },
  subscriptions: {
    taskUnavailable: 'Task is no longer public or active',
    title: 'My subscriptions',
    subtitle: 'Manage your subscribed public tasks and notification settings.',
    totalSubscriptions: 'Total subscriptions',
    notificationsEnabled: 'Notifications enabled',
    notificationsDisabled: 'Notifications disabled',
    emailConfigs: 'Email configs',
    guideLine1: 'When notifications are enabled, you will receive an email when subscribed task content changes.',
    guideLine2: 'You can choose a separate email configuration for each subscription.',
    guideLine3: 'When notifications are disabled, emails stop but the subscription record remains.',
    listTitle: 'My subscriptions',
    noSubscriptions: 'No subscriptions yet',
    noSubscriptionsSubtitle: 'Visit the public task marketplace to subscribe to tasks you care about.',
    browsePublicTasks: 'Browse public tasks',
    taskInfo: 'Task info',
    taskUrl: 'Task URL',
    owner: 'Task owner',
    emailConfig: 'Email config',
    subscribedAt: 'Subscribed at',
    actions: 'Actions',
    unknownConfig: 'Unknown config',
    defaultConfig: 'Default config',
    editSubscription: 'Edit subscription',
    deleteSubscription: 'Cancel subscription',
    editDialogTitle: 'Edit subscription settings',
    taskLabel: 'Task',
    enableEmailNotifications: 'Enable email notifications',
    emailConfigLabel: 'Email config',
    useDefaultConfig: 'Use default config',
    cancel: 'Cancel',
    saveSettings: 'Save settings',
    updateSuccess: 'Subscription updated successfully',
    updateFailed: 'Failed to update subscription',
    deleteConfirmTitle: 'Cancel subscription',
    deleteConfirm: 'Are you sure you want to cancel this subscription?',
    deleteSuccess: 'Subscription cancelled successfully',
    deleteFailed: 'Failed to cancel subscription',
    loadFailed: 'Failed to load subscriptions',
  },
  documentation: {
    badge: 'Docs',
    openNavigation: 'Documentation sections',
    contents: 'Contents',
  },
};

export default en;
